import { Tag, Box, Checkbox } from 'braid-design-system';
import { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { MultiselectAutosuggest } from '../../MultiselectAutosuggest';
import type { Suggestion } from '../types';

import { DEFAULT_DEBOUNCE_TIMEOUT_MS } from './constants';

interface Props {
  id: string;
  label: string;
  selectedItems: Suggestion[];
  suggestions: Suggestion[];
  placeholder?: string;
  onChange: (newSelectedItems: Suggestion[]) => void;
  onSearchTextChange?: (newSearchText: string) => void;
  onDebouncedSearchTextChange?: (newSearchText: string) => void;
}

export const SearchFilter = ({
  id,
  label,
  selectedItems,
  suggestions,
  placeholder,
  onSearchTextChange,
  onDebouncedSearchTextChange,
  onChange,
}: Props) => {
  const [searchText, setSearchText] = useState('');

  const debouncedOnSearchTextChange = useDebouncedCallback(
    onDebouncedSearchTextChange ?? (() => {}),
    DEFAULT_DEBOUNCE_TIMEOUT_MS,
  );

  const handleItemsSelected = (newSelectedItems: Suggestion[]) => {
    onChange?.(newSelectedItems);
  };

  const handleItemOnClear = (itemToClear: Suggestion) => {
    const newItemsSelected = selectedItems.filter(
      (selectedItem) => selectedItem.id !== itemToClear.id,
    );
    onChange?.(newItemsSelected);
  };

  const handleSuggestionOnClick = (item: Suggestion) => {
    const isSelected = isItemSelected(item, selectedItems);

    if (isSelected) {
      const newItemsSelected = selectedItems.filter(
        (selectedItem) => item.name !== selectedItem.name,
      );
      onChange?.(newItemsSelected);
      return;
    }

    const newItemsSelected = [...selectedItems, item];
    onChange?.(newItemsSelected);
  };

  const handleSetSearchText = (newSearchText: string) => {
    setSearchText(newSearchText);

    if (onDebouncedSearchTextChange) {
      debouncedOnSearchTextChange(newSearchText);
    }

    onSearchTextChange?.(newSearchText);
  };

  return (
    <MultiselectAutosuggest
      id={id}
      label={label}
      size="small"
      placeholder={placeholder}
      suggestions={suggestions}
      selectedItems={selectedItems}
      searchText={searchText}
      setSearchText={handleSetSearchText}
      onItemsSelected={handleItemsSelected}
      renderSelectedItems={() => (
        <>
          {selectedItems.map((item, index) => (
            <Box key={index} style={{ maxWidth: '100%' }}>
              <Tag
                size="small"
                clearLabel={`Clear ${item.name}`}
                onClear={() => {
                  handleItemOnClear(item);
                }}
              >
                {item.name}
              </Tag>
            </Box>
          ))}
        </>
      )}
      renderSuggestions={({ getItemProps, highlightedIndex }) => {
        if (!searchText) {
          return <></>;
        }

        return (
          <>
            {suggestions.map((suggestion, index) => {
              const isSelected = isItemSelected(suggestion, selectedItems);

              const suggestionId = `suggestion-${suggestion.id}-${index}`;

              return (
                <Box
                  key={suggestionId}
                  padding="small"
                  cursor="pointer"
                  background={
                    highlightedIndex === index
                      ? 'formAccentSoftHover'
                      : 'surface'
                  }
                  {...getItemProps({
                    item: suggestion,
                  })}
                >
                  <Checkbox
                    size="small"
                    id={suggestionId}
                    label={suggestion.name}
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation();
                    }}
                  />
                </Box>
              );
            })}
          </>
        );
      }}
      onClickSuggestionItem={handleSuggestionOnClick}
    />
  );
};

const isItemSelected = (item: Suggestion, selectedItems: Suggestion[]) =>
  selectedItems.some((selectedItem) => selectedItem.name === item.name);
